/**
 * 🔒 LOCKED — March 29, 2026
 * PlacementAssessmentPage — 6-question structured placement assessment.
 * APPROVED BY OWNER. DO NOT MODIFY without explicit approval.
 *
 * Changes March 29: Added placementEvalFn ref; pre-kicks placementEval + TTS
 * during submit gesture so results page has zero-delay eval and audio.
 * Clears om_placement_result cache on submit to prevent stale demoChat output.
 *
 * Changes March 30: Added DEV-ONLY ⚡ Quick Fill button (activated via ?__devfill=1 URL param).
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { validateAssessmentAnswer } from '../utils/inputValidation';
import { getFunctions, httpsCallable } from 'firebase/functions';

// ── Dev-only quick fill (never ships to production) ───────────────────────
const DEV_QUICK_ANSWERS = [
	// Q1 — OBJ_SYSTEM: describe a repeatable multi-step workflow
	'My repeatable process for client proposals has three defined steps. First I feed AI a structured brief: the client industry, the problem they stated, the outcome they want, and any constraints like budget or timeline. Second, I ask for a draft structured as situation-problem-solution with a clear ask at the end. Third, I do one revision pass where I tell it specifically what is off — usually the tone is too formal or the problem statement is too generic — and I give it a corrected sentence to match before asking it to rewrite only that section. That sequence gives me a usable draft in two passes consistently instead of five.',
	// Q2 — OBJ_CONTEXT: understanding of complete context and what to omit
	'I include anything that constrains the output: the goal, the reader, the format, the length, and any hard limits like things to avoid. I also include a concrete example of what good looks like when I have one. What I leave out is background the AI cannot use — like the history of my relationship with a client or internal politics — because it adds noise without changing the output. The test I use is whether removing a piece of information would cause the AI to produce a materially different result. If not, I cut it.',
	// Q3 — OBJ_ITERATION: deliberate iteration, diagnose before retrying
	'I stop and diagnose before I change anything. I ask myself whether the AI misunderstood the goal, the audience, the format, or the constraints — those are the four things that account for almost every miss I have seen. Once I identify which one is off, I make a single targeted change to that part of the prompt and rerun it. I do not rewrite the whole prompt because that makes it impossible to know what fixed it. If the output is still off after two targeted changes, I break the task into smaller sub-tasks and run them separately, then combine the results myself.',
	// Q4 — OBJ_BOUNDARIES: explicit constraints and output rules
	'Yes. I keep a standing set of output rules I apply to almost every request: no bullet points unless I ask for them, no hedging language like "it is worth noting" or "it depends," responses capped at the length I specify, and no questions back to me unless I ask for them. For client-facing work I also add a rule that the AI should never invent specifics it does not have — it should flag gaps instead of filling them with plausible-sounding details. Those constraints are in a saved template I paste at the top of any high-stakes prompt.',
	// Q5 — OBJ_REPEATABLE: handoff-ready documented workflow
	'I would hand them a written brief template and a one-page process doc. The template has labeled fields: goal, audience, format, length limit, things to avoid, and an example of a good output. The process doc has three steps: fill the template before writing any prompt, run the prompt, then score the output against the original goal before accepting it. I would also show them my revision protocol — identify which of the four failure types it is, change only that part, rerun once. That is the full system. Anyone following those steps gets the same quality I get without needing to develop intuition first.',
	// Q6 — OBJ_DIAGNOSIS: diagnose why output technically answers but misses the point
	'The most common cause is that I stated the task but not the goal. The AI optimizes for the literal request and has no way to know the actual outcome I need. For example, if I ask for a project status summary and what I actually need is something that will reassure a nervous client, those are different documents — but the AI produces the literal one unless I tell it the real purpose. The fix is to add a one-sentence goal statement before the task: "The purpose of this is to reassure the client that we are on track, so lead with confidence not detail." That single addition changes the entire output.',
];

const TIER_ACCENT = {
	foundations: '#38bdf8',
	operator: '#34d399',
	certified: '#a78bfa',
};

/**
 * Build the 6 assessment questions.
 * Q1–Q3: built around the learner's stated focus topic.
 * Q4–Q6: fundamentals-bypass — test whether the learner understands
 *         constraints, structure, and directing AI output (they may
 *         describe these concepts without using exact terminology).
 */
function buildQuestions() {
	return [
		{
			id: 1,
			category: 'focus',
			text: `Based on the area you described, walk me through how you actually use AI in that work right now. Give me a specific example — what you asked for, what you got back, and what you did with the result.`,
		},
		{
			id: 2,
			category: 'fundamentals',
			text: `When you give AI a task, how do you decide what information to include in your request and what to leave out? Describe your thinking process.`,
		},
		{
			id: 3,
			category: 'focus',
			text: `When the AI output in that area isn't what you need, what do you actually do next? Walk me through your real process for getting closer to a usable result.`,
		},
		{
			id: 4,
			category: 'fundamentals',
			text: `Have you ever set boundaries or rules for how the AI should respond — things it should avoid, formats it should follow, or limits on what it should do? Give an example if so.`,
		},
		{
			id: 5,
			category: 'focus',
			text: `If you were handing off your AI workflow in that area to a colleague, what would you tell them so they could get the same quality results you get?`,
		},
		{
			id: 6,
			category: 'fundamentals',
			text: `When you get a response from AI that technically answers your question but misses the point, what's usually the reason? How do you fix it?`,
		},
	];
}

export default function PlacementAssessmentPage() {
	const location = useLocation();
	const navigate = useNavigate();

	// Pull context from MicroAssessmentPage
	const navContext = location.state?.assessmentContext?.micro || null;
	const [storedContext] = useState(() => {
		try {
			const raw = JSON.parse(sessionStorage.getItem('om_assessment_context') || '{}');
			return raw?.micro || null;
		} catch {
			return null;
		}
	});
	const ctx = navContext || storedContext;
	const focusTopic = ctx?.focusTopic || '';
	const activeTier = ctx?.activeTier || 'foundations';
	const accent = TIER_ACCENT[activeTier] || TIER_ACCENT.foundations;

	const [questions] = useState(() => buildQuestions());
	const [answers, setAnswers] = useState(() => questions.map(() => ''));
	const [submitted, setSubmitted] = useState(false);
	const [curtainVisible, setCurtainVisible] = useState(true);
	const [curtainOpacity, setCurtainOpacity] = useState(1);
	const [speedWarning, setSpeedWarning] = useState('');

	// ── Behavioral tracking ──────────────────────────────────────────
	const pageLoadTime = useRef(Date.now());
	const firstKeystroke = useRef(Array(6).fill(null));  // per-question first keystroke ts
	const pasteCount = useRef(Array(6).fill(0));          // per-question paste event count
	const keystrokeCount = useRef(Array(6).fill(0));      // per-question keystroke count

	const trackKeystroke = useCallback((index) => {
		if (!firstKeystroke.current[index]) firstKeystroke.current[index] = Date.now();
		keystrokeCount.current[index]++;
	}, []);

	const trackPaste = useCallback((index) => {
		pasteCount.current[index]++;
	}, []);

	// ── Live security terminal feed ──────────────────────────────────
	const [scanLines, setScanLines] = useState(() => {
		const t = new Date().toTimeString().slice(0, 8);
		return [
			`[${t}] [SYSTEM] Integrity monitor initialized`,
			`[${t}] [SCAN] Input quality gate: armed`,
			`[${t}] [SCAN] Prompt injection filter: active`,
			`[${t}] [INTEGRITY] Behavioral fingerprint: generating`,
		];
	});
	const scanRef = useRef(null);
	const lastScanState = useRef({ keystrokes: 0, pastes: 0, answered: 0 });
	const audioRef = useRef(null);
	const openaiTTSFn = useRef(httpsCallable(getFunctions(), 'openaiTTS'));
	const placementEvalFn = useRef(httpsCallable(getFunctions(), 'placementEval'));

	useEffect(() => {
		const interval = setInterval(() => {
			const t = new Date().toTimeString().slice(0, 8);
			const totalK = keystrokeCount.current.reduce((a, b) => a + b, 0);
			const totalP = pasteCount.current.reduce((a, b) => a + b, 0);
			const elapsed = Math.round((Date.now() - pageLoadTime.current) / 1000);
			const prev = lastScanState.current;
			const newLines = [];

			// Keystroke delta
			if (totalK > prev.keystrokes) {
				const delta = totalK - prev.keystrokes;
				newLines.push(`[${t}] [INPUT] +${delta} keystrokes detected (total: ${totalK})`);
			}

			// Paste event
			if (totalP > prev.pastes) {
				const which = pasteCount.current.findIndex((p, i) =>
					p > 0 && (prev.pasteSnapshot ? p > prev.pasteSnapshot[i] : true)
				);
				newLines.push(`[${t}] [INPUT] \u26a0 Paste event on answer ${which + 1} — clipboard content captured`);
			}

			// New answer completed
			const currentAnswered = answers.filter((a, i) => {
				const trimmed = a.trim();
				return trimmed.length >= 15 && trimmed.split(/\s+/).length >= 3;
			}).length;
			if (currentAnswered > prev.answered) {
				const idx = answers.findIndex((a, i) => {
					const trimmed = a.trim();
					return trimmed.length >= 15 && trimmed.split(/\s+/).length >= 3;
				});
				const snippet = answers[idx]?.trim().slice(0, 60);
				if (snippet) {
					newLines.push(`[${t}] [VERIFY] Inspecting answer ${idx + 1}...`);
					newLines.push(`[${t}] [ECHO] "${snippet}${answers[idx]?.trim().length > 60 ? '...' : ''}"`);
					const wc = answers[idx].trim().split(/\s+/).length;
					newLines.push(`[${t}] [AUTHENTICITY] ${wc} words | classifying originality...`);
				}
			}

			// Ambient scan lines when nothing is happening
			if (newLines.length === 0) {
				const ambientPool = [
					`[${t}] [TIMING] Session elapsed: ${elapsed}s`,
					`[${t}] [INTEGRITY] Behavioral hash: updating`,
					`[${t}] [SCAN] Keystroke pattern analysis: running`,
					`[${t}] [VERIFY] Answer coherence check: standby`,
					`[${t}] [SCAN] Rate limit status: nominal`,
					`[${t}] [INTEGRITY] Daily assessment quota: checked`,
					`[${t}] [VERIFY] Input classifier: monitoring`,
				];
				newLines.push(ambientPool[Math.floor(Math.random() * ambientPool.length)]);
			}

			lastScanState.current = {
				keystrokes: totalK,
				pastes: totalP,
				answered: currentAnswered,
				pasteSnapshot: [...pasteCount.current],
			};

			setScanLines(prev => [...prev.slice(-60), ...newLines]);
		}, 2200);
		return () => clearInterval(interval);
	}, [answers]);

	// Auto-scroll the terminal
	useEffect(() => {
		if (scanRef.current) {
			scanRef.current.scrollTop = scanRef.current.scrollHeight;
		}
	}, [scanLines]);

	// Redirect if no context
	useEffect(() => {
		if (!ctx) {
			navigate('/micro-assessment', { replace: true });
		}
	}, [ctx, navigate]);

	// Cinematic curtain + auto-play TTS intro
	useEffect(() => {
		const fadeStart = setTimeout(() => setCurtainOpacity(0), 300);
		const fadeEnd = setTimeout(() => setCurtainVisible(false), 1800);

		// Consume the pre-fetched TTS promise that was started during the
		// "Continue" button click on MicroAssessmentPage (a user gesture).
		// .then() inherits that activation — browser allows play() here.
		const ttsPromise = window.__omPlacementTTS;
		if (ttsPromise) {
			window.__omPlacementTTS = null;
			ttsPromise.then(result => {
				const b64 = result?.data?.audio;
				if (!b64) return;
				const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
				const blob = new Blob([bytes], { type: result?.data?.mimeType || 'audio/mpeg' });
				const url = URL.createObjectURL(blob);
				const player = new Audio(url);
				audioRef.current = player;
				player.play().catch(() => {});
			}).catch(() => {});
		}

		return () => { clearTimeout(fadeStart); clearTimeout(fadeEnd); };
	}, []);

	const playTts = useCallback(async (text) => {
		if (!text || ctx?.tts?.muted !== false || ctx?.tts?.provider !== 'openai') return;
		const voice = ctx?.tts?.openaiVoice || 'nova';
		if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
		try {
			const result = await openaiTTSFn.current({ text, voice });
			const b64 = result?.data?.audio;
			if (!b64) return;
			const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
			const blob = new Blob([bytes], { type: result?.data?.mimeType || 'audio/mpeg' });
			const url = URL.createObjectURL(blob);
			const player = new Audio(url);
			audioRef.current = player;
			player.play().catch(() => {});
		} catch { /* non-fatal */ }
	}, [ctx]);

	// Stop audio on unmount
	useEffect(() => () => { audioRef.current?.pause(); }, []);

	function updateAnswer(index, value) {
		if (speedWarning) setSpeedWarning('');
		setAnswers((prev) => {
			const next = [...prev];
			next[index] = value;
			return next;
		});
	}

	const answerValidations = answers.map((a) => validateAssessmentAnswer(a));
	const allAnswered = answerValidations.every((v) => v.valid);
	const answeredCount = answerValidations.filter((v) => v.valid).length;

	function handleDevQuickFill() {
		pageLoadTime.current = Date.now() - 130000; // backdate 130 s to pass speed gate
		firstKeystroke.current = DEV_QUICK_ANSWERS.map((_, i) => Date.now() - (125000 - i * 3000));
		keystrokeCount.current = DEV_QUICK_ANSWERS.map((a) => a.length);
		pasteCount.current = Array(6).fill(0);
		setAnswers([...DEV_QUICK_ANSWERS]);
	}

	function handleSubmit() {
		if (!allAnswered || submitted) return;

		// ── Behavioral analysis ──────────────────────────────────
		const submitTime = Date.now();
		const totalSeconds = Math.round((submitTime - pageLoadTime.current) / 1000);
		const totalPastes = pasteCount.current.reduce((a, b) => a + b, 0);
		const totalKeystrokes = keystrokeCount.current.reduce((a, b) => a + b, 0);
		const pastedOnlyCount = pasteCount.current.filter((p, i) => p > 0 && keystrokeCount.current[i] < 5).length;

		// Speed gate: 6 substantive questions in under 2 minutes is suspicious
		const MIN_SECONDS = 120;
		if (totalSeconds < MIN_SECONDS) {
			setSpeedWarning(`You completed 6 assessment questions in ${totalSeconds} seconds. The examiner expects thoughtful, considered responses. Please review your answers before submitting.`);
			return;
		}

		setSubmitted(true);

		const behavioral = {
			totalSeconds,
			firstKeystrokeDeltas: firstKeystroke.current.map((ts) => ts ? Math.round((ts - pageLoadTime.current) / 1000) : null),
			keystrokesPerAnswer: [...keystrokeCount.current],
			pastesPerAnswer: [...pasteCount.current],
			pastedOnlyCount,
			totalPastes,
			totalKeystrokes,
		};

		const payload = {
			source: 'placement-assessment',
			activeTier,
			focusTopic,
			tts: ctx?.tts || { muted: true },
			questions: questions.map((q, i) => ({
				id: q.id,
				category: q.category,
				text: q.text,
				answer: answers[i].trim(),
			})),
			behavioral,
			timestamp: submitTime,
		};

		try {
			// Clear any cached results from previous runs so results page
			// always calls placementEval fresh, never loads stale demoChat output.
			sessionStorage.removeItem('om_placement_result');
			sessionStorage.setItem('om_assessment_context', JSON.stringify({
				micro: ctx,
				placement: payload,
			}));
		} catch {
			// Non-fatal
		}

		// Pre-kick evaluation + TTS while still in user gesture context.
		// PlacementResultsPage consumes these promises to avoid a duplicate call
		// and to play TTS via the gesture-originated promise chain.
		const evalAnswers = payload.questions.map(q => ({ text: q.text, answer: q.answer }));
		window.__omEvalPromise = placementEvalFn.current({ answers: evalAnswers, focusTopic: payload.focusTopic || '' });

		const ttsCtx = ctx?.tts;
		if (ttsCtx?.provider === 'openai' && ttsCtx?.muted !== true) {
			const voice = ttsCtx?.openaiVoice || 'echo';
			window.__omResultsTTS = window.__omEvalPromise
				.then(r => {
					const strengths = r?.data?.strengths;
					const strengthLine = Array.isArray(strengths) && strengths.length
						? `Here's what stood out: ${strengths.slice(0, 2).join('. ')}.`
						: '';
					const text = `Assessment complete. ${strengthLine} Register to unlock your full results.`.trim();
					if (!text) return null;
					return openaiTTSFn.current({ text, voice });
				})
				.catch(() => null);
		} else {
			window.__omResultsTTS = null;
		}

		navigate('/results', {
			state: {
				assessmentContext: {
					micro: ctx,
					placement: payload,
				},
			},
		});
	}

	if (!ctx) return null;

	return (
		<main
			style={{
				minHeight: '100vh',
				color: '#e9f3ff',
				padding: '28px 20px',
				background:
					'repeating-linear-gradient(-24deg, rgba(152,236,255,0.05) 0, rgba(152,236,255,0.05) 1px, transparent 1px, transparent 84px), radial-gradient(120% 80% at 18% 16%, rgba(56,189,248,0.2), transparent 52%), radial-gradient(120% 90% at 76% 74%, rgba(167,139,250,0.16), transparent 58%), linear-gradient(165deg, #040716 0%, #091130 44%, #080f26 100%)',
			}}
		>
			{curtainVisible && (
				<div
					style={{
						position: 'fixed',
						inset: 0,
						zIndex: 9999,
						background: 'linear-gradient(165deg, #040716 0%, #091130 44%, #080f26 100%)',
						opacity: curtainOpacity,
						transition: 'opacity 1.4s ease-in-out',
						pointerEvents: 'none',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
					}}
				>
					<div style={{ fontSize: '0.72rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(155, 233, 255, 0.5)' }}>
						Loading Assessment
					</div>
				</div>
			)}

			<div style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gap: '18px', gridTemplateColumns: 'minmax(0, 1fr) 320px', alignItems: 'start', filter: 'drop-shadow(0 22px 40px rgba(1,6,20,0.38))' }}>
				<div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
				{/* Focus topic carryover */}
				<div style={{
					border: '1px solid rgba(52,211,153,0.35)',
					borderRadius: '10px',
					padding: '10px 14px',
					background: 'rgba(52,211,153,0.06)',
					marginBottom: '16px',
					display: 'flex',
					alignItems: 'center',
					gap: '10px',
				}}>
					<div style={{ fontSize: '0.72rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: '#6ee7b7', whiteSpace: 'nowrap' }}>
						Your Focus
					</div>
					<div style={{ color: '#a7f3d0', fontSize: '0.88rem', lineHeight: 1.4 }}>
						{focusTopic}
					</div>
				</div>

				{/* Instructor instructions */}
				<section style={{
					border: '1px solid rgba(153,179,214,0.22)',
					borderRadius: '14px',
					padding: '20px',
					background: 'linear-gradient(165deg, rgba(8,18,44,0.9), rgba(7,12,29,0.7))',
					boxShadow: '0 34px 78px rgba(1,6,20,0.58), 0 10px 22px rgba(1,6,20,0.38), inset 0 1px 0 rgba(233,243,255,0.09)',
					marginBottom: '20px',
				}}>
					<h1 style={{ margin: '0 0 10px 0', fontSize: '1.3rem' }}>Placement Assessment</h1>
					<p style={{ margin: '0 0 8px 0', color: '#99b3d6', lineHeight: 1.6, fontSize: '0.92rem' }}>
					Answer each question as completely and honestly as you can. Describe what you actually do — not what you think the “right” answer is. There are no right answers.
				</p>
				<p style={{ margin: 0, color: '#d0ab62', lineHeight: 1.6, fontSize: '0.86rem', fontWeight: 500 }}>
					Read all 6 before you start. Keep each answer focused on that question only.
					</p>
				</section>

				{/* Questions */}
				<div style={{ display: 'grid', gap: '16px', marginBottom: '24px' }}>
					{questions.map((q, i) => {
						return (
							<section
								key={q.id}
								style={{
									border: '1px solid rgba(153,179,214,0.2)',
									borderRadius: '12px',
									padding: '14px 16px',
									background: 'linear-gradient(165deg, rgba(8,18,44,0.85), rgba(7,12,29,0.7))',
									boxShadow: 'inset 0 1px 0 rgba(233,243,255,0.06)',
								}}
							>
								<div style={{ marginBottom: '8px' }}>
									<span style={{ fontSize: '0.72rem', color: '#7f95b6' }}>
										Question {q.id} of 6
									</span>
								</div>
								<p style={{ margin: '0 0 10px 0', color: '#d9ecff', fontSize: '0.9rem', lineHeight: 1.55 }}>
									{q.text}
								</p>
								<textarea
									value={answers[i]}
									onChange={(e) => updateAnswer(i, e.target.value)}
									onKeyDown={() => trackKeystroke(i)}
									onPaste={() => trackPaste(i)}
									onFocus={() => !submitted && playTts(q.text)}
									placeholder="Type your answer here..."
									rows={3}
									disabled={submitted}
									style={{
										width: '100%',
										background: 'rgba(9,17,40,0.9)',
										border: `1px solid ${answerValidations[i].valid ? 'rgba(52,211,153,0.35)' : (answers[i].trim() && answerValidations[i].reason ? 'rgba(248,113,113,0.4)' : 'rgba(153,179,214,0.3)')}`,
										borderRadius: '10px',
										padding: '10px 12px',
										color: '#e9f3ff',
										fontSize: '0.88rem',
										lineHeight: 1.5,
										resize: 'vertical',
										transition: 'border-color 200ms ease',
									}}
								/>
								{answerValidations[i].valid && (
									<div style={{ fontSize: '0.7rem', color: '#6ee7b7', marginTop: '4px', letterSpacing: '0.04em' }}>
										&#10003; Answered
									</div>
								)}
								{!answerValidations[i].valid && answers[i].trim() && answerValidations[i].reason && (
									<div style={{ fontSize: '0.72rem', color: '#f87171', marginTop: '4px', lineHeight: 1.4 }}>
										{answerValidations[i].reason}
									</div>
								)}
							</section>
						);
					})}
				</div>

				{/* Submit section */}
				<div style={{
					border: `1px solid ${accent}55`,
					borderRadius: '12px',
					padding: '16px',
					background: `linear-gradient(145deg, ${accent}1f, rgba(8, 16, 34, 0.72))`,
					boxShadow: `0 26px 54px rgba(1,6,20,0.5), 0 0 18px ${accent}18`,
					marginBottom: '16px',
				}}>
					<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
						<div style={{ fontSize: '0.82rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: '#c8eeff' }}>
							{submitted ? 'Submitting...' : `${answeredCount} of 6 answered`}
						</div>
						{!allAnswered && !submitted && (
							<div style={{ fontSize: '0.78rem', color: '#7f95b6' }}>
								Answer all 6 to continue
							</div>
						)}
					</div>

					<p style={{ margin: '0 0 10px 0', color: '#9cb2d3', fontSize: '0.84rem', lineHeight: 1.5 }}>
						Once you submit, your answers go directly to the AI examiner. Review your responses above before continuing — you will not be able to edit them after submission.
					</p>

				{speedWarning && (
					<div style={{
						border: '1px solid rgba(248,113,113,0.4)',
						borderRadius: '10px',
						padding: '10px 14px',
						background: 'rgba(248,113,113,0.08)',
						marginBottom: '10px',
					}}>
						<div style={{ fontSize: '0.82rem', color: '#f87171', lineHeight: 1.5 }}>
							{speedWarning}
						</div>
					</div>
				)}

				<div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
					<button
						type="button"
						onClick={handleSubmit}
						disabled={!allAnswered || submitted}
							style={{
								border: '1px solid rgba(56,189,248,0.82)',
								background: allAnswered && !submitted
									? 'linear-gradient(145deg, rgba(56,189,248,0.38), rgba(56,189,248,0.18))'
									: 'rgba(56,189,248,0.08)',
								color: allAnswered && !submitted ? '#e9f3ff' : '#5a7a9e',
								borderRadius: '10px',
								padding: '10px 18px',
								fontWeight: 600,
								fontSize: '0.92rem',
								cursor: allAnswered && !submitted ? 'pointer' : 'not-allowed',
								boxShadow: allAnswered && !submitted ? '0 0 14px rgba(56,189,248,0.35)' : 'none',
								transition: 'all 200ms ease',
							}}
						>
							{submitted ? 'Submitting Assessment...' : 'Submit Assessment'}
						</button>
						{!submitted && (
							<button
								type="button"
								onClick={() => navigate(-1)}
								style={{
									border: '1px solid rgba(153,179,214,0.3)',
									background: 'transparent',
									color: '#99b3d6',
									borderRadius: '10px',
									padding: '10px 14px',
									fontWeight: 600,
									cursor: 'pointer',
									fontSize: '0.84rem',
								}}
							>
								&#8592; Back
							</button>
						)}
						{new URLSearchParams(location.search).get('__devfill') === '1' && !submitted && (
							<button
								type="button"
								onClick={handleDevQuickFill}
								style={{
									border: '1px solid rgba(251,191,36,0.5)',
									background: 'rgba(251,191,36,0.1)',
									color: '#fbbf24',
									borderRadius: '10px',
									padding: '10px 14px',
									fontWeight: 600,
									cursor: 'pointer',
									fontSize: '0.84rem',
								}}
							>
								&#9889; Dev Fill
							</button>
						)}
					</div>
				</div>

				</div>

				{/* Right sidebar: Integrity Monitor */}
				<aside style={{ position: 'sticky', top: '28px' }}>
				<div style={{
					border: '1px solid rgba(56,189,248,0.18)',
					borderRadius: '14px',
					background: 'linear-gradient(165deg, rgba(8,18,44,0.9), rgba(7,12,29,0.78))',
					padding: '14px',
					boxShadow: '0 34px 78px rgba(1,6,20,0.58), 0 12px 24px rgba(1,6,20,0.42), 0 0 26px rgba(56,189,248,0.09), inset 0 1px 0 rgba(233,243,255,0.08)',
					maxHeight: 'calc(100vh - 80px)',
					overflow: 'hidden',
					display: 'flex',
					flexDirection: 'column',
				}}>
					<div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
						<span style={{
							width: '6px',
							height: '6px',
							borderRadius: '50%',
							background: '#38bdf8',
							boxShadow: '0 0 6px rgba(56,189,248,0.6)',
							animation: 'none',
						}} />
						<span style={{
							fontSize: '0.68rem',
							letterSpacing: '0.1em',
							textTransform: 'uppercase',
							color: 'rgba(56,189,248,0.7)',
						}}>
							Integrity Monitor — Live
						</span>
					</div>
					<div
						ref={scanRef}
						style={{
							overflowY: 'auto',
							flex: 1,
							minHeight: 0,
							fontFamily: "'JetBrains Mono', 'SFMono-Regular', Consolas, monospace",
							fontSize: '0.66rem',
							lineHeight: 1.55,
							color: 'rgba(155,233,255,0.45)',
							userSelect: 'none',
						}}
					>
						{scanLines.map((line, i) => (
							<div key={i} style={{
								opacity: line.includes('\u26a0') ? 0.85 : 0.5,
								color: line.includes('\u26a0') ? 'rgba(248,113,113,0.7)'
									: line.includes('[ECHO]') ? 'rgba(52,211,153,0.6)'
									: undefined,
							}}>
								{line}
							</div>
						))}
					</div>
				</div>
				</aside>
			</div>
		</main>
	);
}
